/**
 * Navigation-state contracts shared between auth pages.
 *
 * Register → Login handoff when Google sign-in reports email_exists (the
 * account predates Google and must sign in via email OTP). One module, both
 * sides import it, so the shape cannot drift between the pages.
 */
export interface GoogleEmailExistsState {
  googleEmailExists?: {
    email: string;
    /** Carried so the OTP login that follows can link Google in the same pass. */
    googleCredential?: string;
  };
}

/**
 * Register → Login handoff when registration hit a duplicate (409).
 *
 * The 409 does not say which identifier collided, so the carried email is only a
 * prefill convenience — the notice tells the user to sign in with the address they
 * first registered with, which is the one the code will be mailed to. Login already
 * resumes a PENDING_VERIFICATION account by re-sending a REGISTRATION code, so no
 * separate resume endpoint is needed on this path.
 */
export interface AccountExistsState {
  accountExists?: {
    email: string;
  };
}

/**
 * Where a route guard sent the merchant from, so the sign-in that follows can return
 * them there. `ProtectedRoute` sets it (`state={{ from: location }}`) when a signed-out
 * merchant opens a protected URL, such as the invoice link in a billing email. Login
 * forwards it to `/auth/verify` and `/auth/pin`, where the post-login chain ends by
 * navigating to it. Without the forwarding the link was honoured only for a merchant
 * who was already signed in.
 */
export interface RedirectFromState {
  from?: {
    pathname: string;
    search?: string;
    hash?: string;
  };
}

/**
 * Everything that can arrive at `/auth/login` via navigation state. Named here rather
 * than spelled inline at the page, so the full set stays discoverable from one module.
 * An intersection (not a union): every member is optional, so any real payload
 * satisfies it and no narrowing is needed before reading a field.
 */
export type LoginNavigationState = GoogleEmailExistsState & AccountExistsState & RedirectFromState;

/**
 * The in-app path a sign-in should land on: the stored location, or the dashboard.
 *
 * Total by design, because the input is not an ordinary prop. `from` lives in
 * `history.state`, which the browser keeps across reloads and across deployments, so a
 * merchant sitting on the sign-in screen when a new version ships replays whatever shape
 * the previous build wrote. Both writers agree today; a shape this function cannot read
 * still has to yield the dashboard rather than throw, because the one caller that matters
 * runs in the render body of the OTP screen and a throw there would strand a merchant
 * mid-sign-in.
 *
 * Only a router location ever reaches here (a path, never a URL), and an auth page is
 * never a landing target, so a stale value cannot send the merchant back into the sign-in
 * screens. Protocol-relative and backslash forms are refused for the same reason a URL is.
 */
export function redirectTargetFrom(from: RedirectFromState['from']): string {
  const pathname = from?.pathname;
  if (typeof pathname !== 'string') {
    return '/';
  }
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.startsWith('/\\')) {
    return '/';
  }
  // Exact path or a child of it, so a future protected route whose name merely begins
  // with "auth" (say /authorisations) is not silently swallowed.
  if (pathname === '/auth' || pathname.startsWith('/auth/')) {
    return '/';
  }
  const search = typeof from?.search === 'string' ? from.search : '';
  const hash = typeof from?.hash === 'string' ? from.hash : '';
  return `${pathname}${search}${hash}`;
}
